import { useMemo, useState } from 'react';
import { formatKwd } from '@/lib/formatPrice';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  useGatheringCategoryBySlug,
  useGatheringPackages,
  usePackageInstructors,
  usePackagesPricing,
  type GatheringPackage,
} from '@/hooks/useGatherings';
import { useSubscriptionDiscounts, discountForFrequency } from '@/hooks/useSubscriptionDiscounts';
import * as Icons from 'lucide-react';
import { Users, Calendar, Sparkles, ArrowRight, ArrowLeft, CheckCircle2, Repeat, Tag, Heart } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FALLBACK_IMAGES } from '@/components/home/GatheringsSection';

type Freq = 'monthly' | 'yearly';
const FREQ_ORDER: Freq[] = ['monthly', 'yearly'];
const FREQ_LABEL_AR: Record<Freq, string> = { monthly: 'شهري', yearly: 'سنوي' };
const FREQ_LABEL_EN: Record<Freq, string> = { monthly: 'Monthly', yearly: 'Yearly' };

const WEEKDAYS_AR = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const WEEKDAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];


interface Props {
  slug?: string;
  embedded?: boolean;
  onClose?: () => void;
}

export default function GatheringCategoryView({ slug, embedded = false, onClose }: Props) {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: category, isLoading: catLoading } = useGatheringCategoryBySlug(slug);
  const { data: packages = [], isLoading: pkgLoading } = useGatheringPackages(category?.id);
  const { data: pricingMap = {} } = usePackagesPricing(packages.map((p) => p.id));
  const { data: discounts } = useSubscriptionDiscounts();

  const [selectedPkg, setSelectedPkg] = useState<GatheringPackage | null>(null);
  const [pkgAttendees, setPkgAttendees] = useState<Record<string, number>>({});
  const [deselectedActivities, setDeselectedActivities] = useState<Set<string>>(new Set());
  const [activeFreq, setActiveFreq] = useState<Freq>('monthly');
  // Base package = first available; tabs scale virtually by frequency
  const basePkg = useMemo(() => packages.find((p) => p.frequency === 'monthly') ?? packages[0] ?? null, [packages]);
  const featuredPkg = selectedPkg ?? basePkg;
  const { data: instructors = [] } = usePackageInstructors(featuredPkg?.id);


  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [weekday, setWeekday] = useState<number>(6);
  const [timeSlot, setTimeSlot] = useState('17:00');
  const [address, setAddress] = useState('');
  const [attendees, setAttendees] = useState(5);
  const [paymentMethod, setPaymentMethod] = useState('knet');
  const [installments, setInstallments] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const uniqueInstructors = useMemo(() => {
    const seen = new Set<string>();
    return instructors.filter((row) => {
      if (seen.has(row.instructor_id)) return false;
      seen.add(row.instructor_id);
      return true;
    });
  }, [instructors]);

  const handleSubscribe = async () => {
    if (!user) {
      toast({ title: isArabic ? 'سجّل الدخول أولاً' : 'Please sign in first', variant: 'destructive' });
      navigate('/auth');
      return;
    }
    if (!selectedPkg) return;
    if (!startDate || !timeSlot || !address.trim()) {
      toast({ title: isArabic ? 'أكمل البيانات' : 'Please fill all fields', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data: sub, error: subErr } = await supabase
        .from('gathering_subscriptions')
        .insert({
          user_id: user.id,
          package_id: selectedPkg.id,
          start_date: startDate,
          time_slot: timeSlot,
          address: address.trim(),
          attendees_count: attendees,
          total_price_kwd: (pricingMap[selectedPkg.id]?.perPerson ?? Number(selectedPkg.price_kwd) * selectedPkg.sessions_count) * Math.max(1, attendees) * (1 - (pricingMap[selectedPkg.id]?.avgDiscount ?? 0) / 100),
          payment_method: paymentMethod,
          is_installment: installments && selectedPkg.installments_allowed,
          installment_count: installments && selectedPkg.installments_allowed
            ? selectedPkg.installment_months
            : null,
          status: 'pending_payment',
        })
        .select()
        .single();
      if (subErr) throw subErr;

      const sessions: any[] = [];
      const firstDate = new Date(startDate + 'T00:00:00');
      const diff = (weekday - firstDate.getDay() + 7) % 7;
      firstDate.setDate(firstDate.getDate() + diff);
      const totalInstructors = instructors.length;
      for (let i = 0; i < selectedPkg.sessions_count; i++) {
        const d = new Date(firstDate);
        const step = selectedPkg.frequency === 'monthly' ? 30 : selectedPkg.frequency === 'biweekly' ? 14 : 7;
        d.setDate(d.getDate() + step * i);
        const ins = totalInstructors > 0 ? instructors[i % totalInstructors] : null;
        sessions.push({
          subscription_id: sub.id,
          session_index: i + 1,
          session_date: d.toISOString().slice(0, 10),
          session_time: timeSlot,
          instructor_id: ins?.instructor_id ?? null,
          activity_id: ins?.activity_id ?? null,
          status: 'scheduled',
        });
      }
      if (sessions.length > 0) {
        await supabase.from('gathering_subscription_sessions').insert(sessions);
      }

      const { data: checkoutData, error: checkoutErr } = await supabase.functions.invoke(
        'create-gathering-checkout',
        { body: { subscription_id: sub.id, payment_method: paymentMethod } },
      );
      if (checkoutErr) throw checkoutErr;
      const url = (checkoutData as any)?.paymentUrl;
      if (!url) throw new Error('No payment URL returned');
      window.location.href = url;
    } catch (e: any) {
      console.error(e);
      toast({
        title: isArabic ? 'تعذر بدء الاشتراك' : 'Could not start subscription',
        description: e?.message || 'Error',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (catLoading) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="h-40 bg-muted animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <h1 className="text-2xl font-bold">{isArabic ? 'لم يتم العثور على الفئة' : 'Category not found'}</h1>
      </div>
    );
  }

  const Icon = (Icons as any)[category.icon] || Users;
  const name = isArabic ? category.name_ar : category.name_en || category.name_ar;
  const desc = isArabic ? category.description_ar : category.description_en || category.description_ar;
  const BackIcon = isArabic ? ArrowRight : ArrowLeft;
  const coverSrc = category.cover_image_url || FALLBACK_IMAGES[category.slug];

  return (
    <div className={embedded ? 'relative' : 'bg-gradient-hero'}>
      {embedded && onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={isArabic ? 'إغلاق' : 'Close'}
          className="absolute top-3 right-3 z-50 inline-flex items-center justify-center w-10 h-10 md:w-9 md:h-9 rounded-full bg-white/95 hover:bg-white text-foreground shadow-lg border border-white/80 backdrop-blur-md transition-transform hover:scale-105 active:scale-95"
        >
          <Icons.X className="w-5 h-5 md:w-4 md:h-4" strokeWidth={2.5} />
        </button>
      )}
      <section className={`relative overflow-hidden border-b border-white/40 ${!coverSrc ? `bg-gradient-to-br ${category.gradient_class}` : ''}`}>
        {coverSrc && (
          <>
            <img src={coverSrc} alt={name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/55 to-background/30" />
          </>
        )}
        <div className={`container mx-auto px-4 ${embedded ? 'py-6 md:py-8' : 'py-10 md:py-14'} relative z-10`}>
          {!embedded && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mb-5 inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold text-foreground/80 hover:text-foreground bg-white/70 backdrop-blur-md border border-white/60 rounded-full px-3 py-1.5 shadow-sm hover:shadow-md transition-all"
            >
              <BackIcon className="w-3.5 h-3.5" />
              {isArabic ? 'رجوع' : 'Back'}
            </button>
          )}
          <div className="flex items-start gap-4 md:gap-6 max-w-3xl">
            <div className="w-14 h-14 md:w-20 md:h-20 rounded-2xl bg-white/80 backdrop-blur flex items-center justify-center shadow-soft shrink-0">
              <Icon className="w-7 h-7 md:w-10 md:h-10 text-foreground" />
            </div>
            <div>
              <h1 className={`${embedded ? 'text-2xl md:text-4xl' : 'text-3xl md:text-5xl'} font-extrabold leading-tight drop-shadow-sm`}>{name}</h1>
              {desc && <p className="mt-2 text-sm md:text-base text-foreground/90 drop-shadow-sm">{desc === 'فعاليات اليوم المفتوح للمدارس مع أنشطة متنوعة' ? 'فعاليات اليوم المفتوح للمدارس' : desc}</p>}
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 md:py-7 pb-2 md:pb-3">
        <div className="container mx-auto px-4">
          {pkgLoading ? (
            <div className="h-56 bg-muted rounded-2xl animate-pulse" />
          ) : !featuredPkg ? (
            <p className="text-muted-foreground text-sm">
              {isArabic ? 'لا توجد باقة متاحة حالياً' : 'No package available right now.'}
            </p>
          ) : (() => {
            const pkg = featuredPkg;
            const chosen = pkgAttendees[pkg.id] ?? 3;
            const pricing = pricingMap[pkg.id];
            // Base = monthly perPerson
            const basePerPerson = pricing?.perPerson ?? Number(pkg.price_kwd) * pkg.sessions_count;
            const baseSessions = pkg.sessions_count || 1;
            // Scale by selected tab
            const freqMultiplier = activeFreq === 'yearly' ? 12 : 1;
            const perPerson = basePerPerson * freqMultiplier;
            const scaledSessions = Math.max(1, Math.round(baseSessions * freqMultiplier));
            const discountPct = discountForFrequency(discounts, activeFreq);
            const grossTotal = chosen > 0 ? perPerson * chosen : 0;
            const netTotal = grossTotal * (1 - discountPct / 100);
            const freqLabel =
              activeFreq === 'monthly'
                ? (isArabic ? 'شهرياً' : 'Monthly')
                : (isArabic ? 'سنوياً' : 'Yearly');
            const sessionsBenefit = isArabic
              ? `${scaledSessions} ${activeFreq === 'monthly' ? 'جلسات شهرياً' : 'جلسة سنوياً'}`
              : `${scaledSessions} sessions / ${freqLabel.toLowerCase()}`;

            return (
              <div className="max-w-2xl mx-auto">
                {discountPct > 0 && (
                  <div className="flex justify-end pb-3">
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-extrabold text-white bg-gradient-to-r from-[#E94B7B] to-[#F39A2B] shadow-md">
                      <Sparkles className="w-3.5 h-3.5" />
                      {isArabic ? `خصم ${Math.round(discountPct)}%` : `${Math.round(discountPct)}% OFF`}
                    </span>
                  </div>
                )}
                <div className="pt-0 space-y-5">
                  <div className="rounded-2xl border border-white/40 bg-white/30 backdrop-blur-xl shadow-sm p-3 space-y-4">
                    <Tabs value={activeFreq} onValueChange={(v) => { setActiveFreq(v as Freq); setSelectedPkg(null); }} className="w-full">
                      <TabsList className="grid grid-cols-2 w-full h-auto p-1 rounded-xl bg-white/50 backdrop-blur-xl border border-white/60">
                        {FREQ_ORDER.map((f) => (
                          <TabsTrigger
                            key={f}
                            value={f}
                            className="rounded-lg py-2 text-sm font-bold transition-all text-foreground/60 hover:text-foreground hover:bg-white/40 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E94B7B] data-[state=active]:to-[#F39A2B] data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02]"
                          >
                            {isArabic ? FREQ_LABEL_AR[f] : FREQ_LABEL_EN[f]}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold">
                          {isArabic ? 'عدد المشاركين' : 'Attendees'}
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          placeholder={isArabic ? 'مثال: 5' : 'e.g. 5'}
                          className="h-11 bg-white/70"
                          value={chosen}
                          onChange={(e) => {
                            const v = Math.max(0, Number(e.target.value) || 0);
                            setPkgAttendees((p) => ({ ...p, [pkg.id]: v }));
                          }}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold">
                          {isArabic ? 'الساعة' : 'Time'}
                        </Label>
                        <Input
                          type="time"
                          className="h-11 bg-white/70"
                          value={timeSlot}
                          onChange={(e) => setTimeSlot(e.target.value)}
                        />
                      </div>
                    </div>
                    {chosen > 0 && (
                      <div className="pt-2 border-t border-white/50 space-y-1">
                        {discountPct > 0 && (
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{isArabic ? 'السعر قبل الخصم' : 'Before discount'}</span>
                            <span className="line-through">{formatKwd(grossTotal)} {isArabic ? 'د.ك' : 'KWD'}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {isArabic ? 'السعر الإجمالي' : 'Total price'}
                          </span>
                          <span className="text-2xl font-extrabold text-primary">
                            {formatKwd(netTotal)} <span className="text-xs">{isArabic ? 'د.ك' : 'KWD'}</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    size="lg"
                    className="w-full text-base font-bold"
                    disabled={chosen <= 0}
                    onClick={() => {
                      setSelectedPkg(pkg);
                      setAttendees(chosen);
                      setOpen(true);
                    }}
                  >
                    {isArabic ? 'اشترك الآن' : 'Subscribe now'}
                  </Button>

                  {(() => {
                    const benefits = isArabic
                      ? [
                          { icon: Calendar, text: sessionsBenefit },
                          { icon: Users, text: category.slug?.includes('school') || category.audience === 'schools' ? 'الأنشطة المدرسية تناسب الأولاد و البنات' : 'أنشطة الزوارة تناسب العائلات' },
                          { icon: Tag, text: discountPct > 0 ? `خصم ${Math.round(discountPct)}% على جميع الأنشطة` : 'خصم على جميع الأنشطة' },
                          { icon: Heart, text: 'تجربة مميزة بمدرب مختلف كل مرة' },
                        ]
                      : [
                          { icon: Calendar, text: sessionsBenefit },
                          { icon: Users, text: 'Suitable for families and kids' },
                          { icon: Tag, text: discountPct > 0 ? `${Math.round(discountPct)}% off all activities` : 'Discount on all activities' },
                          { icon: Heart, text: 'A different host every session' },
                        ];
                    return (
                      <ul className="space-y-2 pr-1">
                        {benefits.map((b, i) => {
                          const I = b.icon;
                          return (
                            <li key={i} className="flex items-center gap-2.5 text-sm md:text-base font-semibold text-foreground">
                              <I className="w-4 h-4 text-[#E94B7B] shrink-0" />
                              <span>{b.text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    );
                  })()}

                  <p className="text-[11px] text-muted-foreground text-center whitespace-pre-line">
                    {isArabic
                      ? "\n"
                      : 'Palette automatically picks suitable activities from this category for your chosen time.'}
                  </p>

                </div>
              </div>
            );
          })()}
        </div>
      </section>


      {featuredPkg && (() => {
        const pickedActivities = pricingMap[featuredPkg.id]?.activities ?? [];
        const freqLabel =
          featuredPkg.frequency === 'biweekly'
            ? (isArabic ? 'كل أسبوعين' : 'Bi-weekly')
            : featuredPkg.frequency === 'monthly'
              ? (isArabic ? 'شهرياً' : 'Monthly')
              : featuredPkg.frequency === 'yearly'
                ? (isArabic ? 'سنوياً' : 'Yearly')
                : (isArabic ? 'أسبوعياً' : 'Weekly');
        return (
          <section className="pt-1 pb-5 md:pt-2 md:pb-8 -mt-3 md:-mt-4">
            <div className="container mx-auto px-4 space-y-6 max-w-2xl">
              <div>
                <div className="flex items-end justify-between mb-3 gap-3">
                  <h2 className="text-base md:text-lg font-bold">
                    {isArabic ? 'الأنشطة المشاركة' : 'Activities included'}
                  </h2>
                  <span className="text-[11px] text-muted-foreground">
                    {isArabic ? 'اضغط لاختيار ما يهمك' : 'Tap to pick what you want'}
                  </span>
                </div>
                {pickedActivities.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {isArabic ? 'لم يتم ربط أنشطة بعد.' : 'No activities linked yet.'}
                  </p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-2">
                    {pickedActivities.map((a) => {
                      const isSelected = !deselectedActivities.has(a.id);
                      const img = a.gallery_urls?.[0];
                      const label = a.title_ar || (isArabic ? 'نشاط' : 'Activity');
                      return (
                        <button
                          type="button"
                          key={a.id}
                          onClick={() => {
                            setDeselectedActivities((prev) => {
                              const next = new Set(prev);
                              if (next.has(a.id)) next.delete(a.id);
                              else next.add(a.id);
                              return next;
                            });
                          }}
                          className={`text-center group transition-all ${isSelected ? '' : 'opacity-40 grayscale'}`}
                        >
                          <div className={`relative aspect-square rounded-xl overflow-hidden bg-muted shadow-sm group-hover:shadow-card transition-shadow ${isSelected ? 'ring-2 ring-[#E94B7B]' : 'ring-1 ring-border'}`}>
                            {img ? (
                              <img src={img} alt={label} loading="lazy" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-primary/10">
                                <Sparkles className="w-5 h-5 text-primary" />
                              </div>
                            )}
                            {isSelected && (
                              <span className="absolute top-1 end-1 bg-[#E94B7B] text-white rounded-full p-0.5 shadow">
                                <CheckCircle2 className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-[10px] md:text-xs font-medium truncate">{label}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>


              <div>
                <div className="flex items-end justify-between mb-2">
                  <h3 className="text-sm md:text-base font-bold text-foreground/80">
                    {isArabic ? 'المدربون و الشركاء' : 'Hosts & partners'}
                  </h3>
                </div>
                {uniqueInstructors.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {isArabic ? 'لم يتم ربط مدربين بهذه الباقة بعد.' : 'No hosts linked to this package yet.'}
                  </p>
                ) : (
                  <div className="grid grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-2">
                    {uniqueInstructors.map((row) => {
                      const profile = row.profile;
                      const label = profile?.display_name || profile?.full_name || (isArabic ? 'مدرب' : 'Host');
                      return (
                        <div key={row.instructor_id} className="text-center group">
                          <div className="aspect-square rounded-xl overflow-hidden bg-muted shadow-sm group-hover:shadow-card transition-shadow">
                            {profile?.avatar_url ? (
                              <img
                                src={profile.avatar_url}
                                alt={label}
                                loading="lazy"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-primary/10">
                                <Users className="w-4 h-4 text-primary" />
                              </div>
                            )}
                          </div>
                          <p className="mt-1 text-[10px] md:text-xs font-medium truncate">{label}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })()}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isArabic ? 'الاشتراك بالباقة' : 'Subscribe to package'}: {selectedPkg ? (isArabic ? selectedPkg.title_ar : selectedPkg.title_en) : ''}
            </DialogTitle>
            <DialogDescription>
              {isArabic
                ? 'اختر تاريخ البداية و الوقت و العنوان، و يتولى النظام جدولة المدربين المختلفين تلقائياً.'
                : 'Pick your start date, time and address; we will rotate hosts automatically.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{isArabic ? 'تاريخ البداية' : 'Start date'}</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <Label>{isArabic ? 'الوقت' : 'Time'}</Label>
                <Input type="time" value={timeSlot} onChange={(e) => setTimeSlot(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>{isArabic ? 'يوم الأسبوع للجلسات' : 'Weekday for sessions'}</Label>
              <Select value={String(weekday)} onValueChange={(v) => setWeekday(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(isArabic ? WEEKDAYS_AR : WEEKDAYS_EN).map((d, i) => (
                    <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                {selectedPkg?.frequency === 'monthly'
                  ? (isArabic ? 'تكرار: شهرياً' : 'Repeats: monthly')
                  : selectedPkg?.frequency === 'biweekly'
                    ? (isArabic ? 'تكرار: كل أسبوعين' : 'Repeats: bi-weekly')
                    : (isArabic ? 'تكرار: أسبوعياً' : 'Repeats: weekly')}
              </p>
            </div>
            <div>
              <Label>{isArabic ? 'العنوان' : 'Address'}</Label>
              <Textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={isArabic ? 'المنطقة، القطعة، الشارع، المنزل' : 'Area, block, street, house'}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{isArabic ? 'عدد الحضور' : 'Attendees'}</Label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={attendees}
                  onChange={(e) => setAttendees(Math.max(1, Number(e.target.value) || 1))}
                />
              </div>
              <div>
                <Label>{isArabic ? 'طريقة الدفع' : 'Payment method'}</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="knet">{isArabic ? 'كي نت' : 'KNET'}</SelectItem>
                    <SelectItem value="visa">Visa / Mastercard</SelectItem>
                    <SelectItem value="apple_pay">Apple Pay</SelectItem>
                    <SelectItem value="google_pay">Google Pay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedPkg?.installments_allowed && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div>
                  <p className="text-sm font-semibold text-emerald-900">
                    {isArabic ? 'الدفع بالتقسيط' : 'Pay in installments'}
                  </p>
                  <p className="text-xs text-emerald-700">
                    {isArabic
                      ? `قسّم المبلغ على ${selectedPkg.installment_months} أشهر`
                      : `Split over ${selectedPkg.installment_months} months`}
                  </p>
                </div>
                <Switch checked={installments} onCheckedChange={setInstallments} />
              </div>
            )}
            {selectedPkg && (() => {
              const pp = pricingMap[selectedPkg.id]?.perPerson ?? Number(selectedPkg.price_kwd) * selectedPkg.sessions_count;
              const disc = pricingMap[selectedPkg.id]?.avgDiscount ?? 0;
              const net = pp * Math.max(1, attendees) * (1 - disc / 100);
              return (
                <div className="p-3 rounded-lg bg-muted/60 text-sm">
                  <div className="flex items-center justify-between">
                    <span>{isArabic ? `الإجمالي (${attendees} مشارك)` : `Total (${attendees} attendees)`}</span>
                    <span className="font-bold">
                      {formatKwd(net)} {isArabic ? 'د.ك' : 'KWD'}
                      {disc > 0 && (
                        <span className="ml-2 text-xs text-emerald-600">
                          {isArabic ? `(خصم ${Math.round(disc)}%)` : `(${Math.round(disc)}% off)`}
                        </span>
                      )}
                    </span>
                  </div>
                  {installments && selectedPkg.installments_allowed && (
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                      <span>{isArabic ? 'أول قسط الآن' : 'First installment now'}</span>
                      <span>
                        {formatKwd(net / selectedPkg.installment_months)} {isArabic ? 'د.ك' : 'KWD'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              {isArabic ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSubscribe} disabled={submitting}>
              {submitting ? (isArabic ? 'جارٍ التحويل...' : 'Redirecting...') : (isArabic ? 'تأكيد و الدفع' : 'Confirm & pay')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
